import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FikoYTService } from '@/src/service/fikoyt.service';

declare var $: any;

/**
 * 🎯 FikoFloatingToolbar - Taşınabilir, Slot-based Toolbar Component
 *
 * Özellikler:
 * - ✅ Draggable (taşınabilir) - Sol handle ile
 * - ✅ Slot-based content (ng-content) - İçine herhangi bir şey eklenebilir
 * - ✅ Multiple instances - Birden fazla toolbar aynı anda
 * - ✅ Show/Hide toggle - Görünürlük kontrolü
 * - ✅ ID-based management - Benzersiz ID ile yönetim
 * - ✅ Cyber/Neon themed - FikoYT tasarım dili
 * - ✅ Position persistence - Konum hafızası (opsiyonel)
 *
 * Kullanım:
 * ```html
 * <fikoyt-floating-toolbar
 *   [toolbarId]="'mainTools'"
 *   [baslik]="'Ana Araçlar'"
 *   [simge]="'/assets/icons/tools.webp'"
 *   [baslangicPozisyonu]="{top: 100, left: 50}"
 *   [gorunur]="true"
 *   (gorunurDegisti)="handleVisibilityChange($event)">
 *
 *   <!-- Slot content - İstediğiniz herhangi bir şey -->
 *   <button class="circular ui icon green tiny button">
 *     <i class="icon pencil"></i>
 *   </button>
 *   <button class="circular ui icon blue tiny button">
 *     <i class="icon eraser"></i>
 *   </button>
 * </fikoyt-floating-toolbar>
 * ```
 */
@Component({
  selector: 'fikoyt-floating-toolbar',
  templateUrl: './fiko-floating-toolbar.component.html',
  styleUrls: ['./fiko-floating-toolbar.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class FikoFloatingToolbarComponent implements AfterViewInit, OnDestroy {

  /** Toolbar'ın benzersiz ID'si - Birden fazla toolbar için gerekli */
  @Input() toolbarId: string = '';

  /** Toolbar başlık metni */
  @Input() baslik: string = 'FikoYT Toolbar';

  /** Başlık yanındaki simge (URL veya Semantic UI icon class) */
  @Input() simge?: string;

  /** Simge tipi: 'url' | 'icon' | 'emoji' */
  @Input() simgeTipi: 'url' | 'icon' | 'emoji' = 'icon';

  /** Başlangıç pozisyonu (px cinsinden) */
  @Input() baslangicPozisyonu?: { top?: number; left?: number; right?: number; bottom?: number };

  /** Görünürlük durumu */
  @Input() gorunur: boolean = true;

  /** Toolbar genişliği (CSS değeri: '200px', 'auto', 'min-content' vb.) */
  @Input() genislik: string = 'auto';

  /** Toolbar yüksekliği (CSS değeri) */
  @Input() yukseklik: string = 'auto';

  /** Draggable handle görünür mü? */
  @Input() handleGorunur: boolean = true;

  /** Kapatma butonu görünür mü? */
  @Input() kapatButonuGorunur: boolean = true;

  /** Minimize/Maximize butonu görünür mü? */
  @Input() minimizeButonuGorunur: boolean = false;

  /** Toolbar minimize edilmiş mi? */
  @Input() minimize: boolean = false;

  /** Başlık alanı görünür mü? (false ise tamamen başlıksız) */
  @Input() baslikGorunur: boolean = true;

  /** Toolbar z-index değeri */
  @Input() zIndex: number = 1000;

  /** Toolbar container'a CSS class ekle */
  @Input() ozelSinif?: string;

  /** Pozisyon değiştiğinde emit */
  @Output() pozisyonDegisti = new EventEmitter<{ top: number; left: number }>();

  /** Görünürlük değiştiğinde emit */
  @Output() gorunurDegisti = new EventEmitter<boolean>();

  /** Minimize durumu değiştiğinde emit */
  @Output() minimizeDegisti = new EventEmitter<boolean>();

  /** Kapatma butonuna tıklandığında emit */
  @Output() kapandi = new EventEmitter<void>();

  @ViewChild('toolbarContainer', { static: false }) toolbarContainer?: ElementRef;

  /** Internal - jQuery modal ID */
  id_dialog: string;

  /** Internal - Toolbar açık mı? */
  miAcik: boolean = false;

  /** Internal - Son pozisyon */
  private sonPozisyon?: { top: number; left: number };

  /** Passive touch listener cleanup */
  private touchStartCleanup: (() => void) | null = null;
  private hostElement = inject(ElementRef);

  constructor(
    public readonly FikoYT: FikoYTService,
    public readonly CD: ChangeDetectorRef
  ) {
    // Benzersiz ID oluştur
    this.id_dialog = "FikoFloatingToolbar_" + this.FikoYT.fikoIdUret();
  }

  ngAfterViewInit(): void {
    // Input'tan gelen toolbarId varsa kullan, yoksa auto-generated ID kullan
    if (this.toolbarId) {
      this.id_dialog = "FikoFloatingToolbar_" + this.toolbarId;
    }

    // Service'e kaydet (görünür olsun ya da olmasın, toggle için gerekli)
    // console.log('[FloatingToolbar] ngAfterViewInit, toolbarId:', this.toolbarId, 'service:', !!this.FikoYT.toolbarService);
    if (this.toolbarId && this.FikoYT.toolbarService) {
      this.FikoYT.toolbarService.kaydet(this.toolbarId, this);
      // console.log('[FloatingToolbar] Kayıt başarılı:', this.toolbarId);
    } else {
      console.warn('[FloatingToolbar] Kayıt yapılamadı! toolbarId:', this.toolbarId, 'service:', this.FikoYT.toolbarService);
    }

    // Toolbar'ı body'ye taşı (dialog overflow sorununu çöz)
    setTimeout(() => {
      const element = document.getElementById(this.id_dialog);
      if (element && element.parentElement?.tagName !== 'BODY') {
        document.body.appendChild(element);
      }
    }, 10);

    // Başlangıçta görünürse aç
    if (this.gorunur) {
      setTimeout(() => this.ac(), 100);
    }

    // Passive touchstart listener (replaces template binding for better performance)
    setTimeout(() => {
      const element = document.getElementById(this.id_dialog);
      if (element) {
        const handler = () => this.oneGetir();
        element.addEventListener('touchstart', handler, { passive: true });
        this.touchStartCleanup = () => element.removeEventListener('touchstart', handler);
      }
    }, 20);
  }

  /**
   * Toolbar'ı aç ve görünür yap
   */
  public ac(): void {
    // console.log('[FloatingToolbar] ac() çağrıldı, id:', this.id_dialog, 'miAcik:', this.miAcik);
    const _this = this;

    // Eğer zaten açıksa sadece göster
    if (this.miAcik) {
      // console.log('[FloatingToolbar] Zaten açık, show() çağrılıyor');
      $("#" + this.id_dialog).show();
      return;
    }

    this.miAcik = true;
    this.gorunur = true;
    this.CD.detectChanges();
    // console.log('[FloatingToolbar] miAcik=true, gorunur=true ayarlandı');

    // Draggable yap
    setTimeout(() => {
      // Öncelik sırası: 1) Handle varsa handle ile 2) Başlık görünürse header ile 3) Tüm toolbar ile
      let draggableConfig: any = {
        stop: (event: any, ui: any) => {
          // Pozisyon değiştiğinde emit et
          _this.sonPozisyon = {
            top: ui.position.top,
            left: ui.position.left
          };
          _this.pozisyonDegisti.emit(_this.sonPozisyon);
        }
      };

      // Handle varsa sadece handle ile taşınabilir
      if (_this.handleGorunur && _this.baslikGorunur) {
        draggableConfig.handle = ".toolbar-handle";
      }
      // Handle yok ama başlık varsa header ile taşınabilir
      else if (_this.baslikGorunur) {
        draggableConfig.handle = ".fiko-toolbar-header";
      }
      // Başlık yoksa tüm toolbar taşınabilir (handle belirtmeyin)

      $("#" + _this.id_dialog).draggable(draggableConfig);

      // Başlangıç pozisyonunu ayarla
      if (_this.baslangicPozisyonu) {
        const css: any = {};
        if (_this.baslangicPozisyonu.top !== undefined) css.top = _this.baslangicPozisyonu.top + 'px';
        if (_this.baslangicPozisyonu.left !== undefined) css.left = _this.baslangicPozisyonu.left + 'px';
        if (_this.baslangicPozisyonu.right !== undefined) css.right = _this.baslangicPozisyonu.right + 'px';
        if (_this.baslangicPozisyonu.bottom !== undefined) css.bottom = _this.baslangicPozisyonu.bottom + 'px';

        $("#" + _this.id_dialog).css(css);
      }

      // Service'e açıldığını bildir ve z-index al
      const yeniZIndex = _this.FikoYT.toolbarService?.toolbarAcildi(_this);
      if (yeniZIndex !== undefined && yeniZIndex !== null) {
        _this.zIndex = yeniZIndex;
        $("#" + _this.id_dialog).css('z-index', yeniZIndex);
      }

      _this.gorunurDegisti.emit(true);
    }, 50);
  }

  /**
   * Toolbar'ı kapat (gizle)
   */
  public kapa(): void {
    // console.log('[FloatingToolbar] kapa() çağrıldı, id:', this.id_dialog);
    $("#" + this.id_dialog).hide();
    this.miAcik = false;
    this.gorunur = false;
    this.gorunurDegisti.emit(false);
    this.kapandi.emit();
    this.CD.detectChanges();
    // console.log('[FloatingToolbar] Toolbar kapatıldı');
  }

  /**
   * Toolbar görünürlüğünü toggle et
   */
  public toggle(): void {
    // console.log('[FloatingToolbar] toggle() çağrıldı, id:', this.id_dialog, 'miAcik:', this.miAcik);
    if (this.miAcik) {
      // console.log('[FloatingToolbar] Kapatılıyor...');
      this.kapa();
    } else {
      // console.log('[FloatingToolbar] Açılıyor...');
      this.ac();
    }
  }

  /**
   * Minimize/Maximize toggle
   */
  public toggleMinimize(): void {
    this.minimize = !this.minimize;
    this.minimizeDegisti.emit(this.minimize);
    this.CD.detectChanges();
  }

  /**
   * Toolbar'ı belirli bir pozisyona taşı
   */
  public pozisyonAyarla(pozisyon: { top?: number; left?: number; right?: number; bottom?: number }): void {
    const css: any = {};
    if (pozisyon.top !== undefined) css.top = pozisyon.top + 'px';
    if (pozisyon.left !== undefined) css.left = pozisyon.left + 'px';
    if (pozisyon.right !== undefined) css.right = pozisyon.right + 'px';
    if (pozisyon.bottom !== undefined) css.bottom = pozisyon.bottom + 'px';

    $("#" + this.id_dialog).css(css);

    if (pozisyon.top !== undefined && pozisyon.left !== undefined) {
      this.sonPozisyon = {
        top: pozisyon.top,
        left: pozisyon.left
      };
      this.pozisyonDegisti.emit(this.sonPozisyon);
    }
  }

  /**
   * Mevcut pozisyonu al
   */
  public pozisyonAl(): { top: number; left: number } | undefined {
    const $element = $("#" + this.id_dialog);
    if ($element.length) {
      const offset = $element.offset();
      return {
        top: offset.top,
        left: offset.left
      };
    }
    return this.sonPozisyon;
  }

  /**
   * Toolbar'ı merkeze getir
   */
  public ortala(): void {
    const $element = $("#" + this.id_dialog);
    if ($element.length) {
      const width = $element.outerWidth() || 0;
      const height = $element.outerHeight() || 0;
      const windowWidth = $(window).width();
      const windowHeight = $(window).height();

      this.pozisyonAyarla({
        top: (windowHeight - height) / 2,
        left: (windowWidth - width) / 2
      });
    }
  }

  /**
   * 🎯 Toolbar'ı öne getir (z-index artır)
   * Tıklandığında en üste çıkması için
   */
  public oneGetir(): void {
    // Service'den yeni z-index al
    const yeniZIndex = this.FikoYT.toolbarService?.toolbarTiklandi(this);

    if (yeniZIndex !== undefined && yeniZIndex !== null) {
      this.zIndex = yeniZIndex;

      // jQuery element'ine de uygula
      const $element = $("#" + this.id_dialog);
      if ($element.length) {
        $element.css('z-index', yeniZIndex);
      }

      this.CD.detectChanges();
    }
  }

  ngOnDestroy(): void {
    // Passive touch listener cleanup
    if (this.touchStartCleanup) {
      this.touchStartCleanup();
      this.touchStartCleanup = null;
    }

    // Service'den toolbar'ı kaldır
    if (this.FikoYT.toolbarService && this.toolbarId) {
      this.FikoYT.toolbarService.toolbarKapandi(this);
    }

    // jQuery draggable'ı temizle
    try {
      if ($("#" + this.id_dialog).data('ui-draggable')) {
        $("#" + this.id_dialog).draggable('destroy');
      }
    } catch (e) {
      // Ignore
    }

    // Body'den elementi kaldır
    try {
      const element = document.getElementById(this.id_dialog);
      if (element && element.parentElement) {
        element.parentElement.removeChild(element);
      }
    } catch (e) {
      // Ignore
    }
  }
}
